import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBiz } from '../BizContext';
import { useEntries } from './useEntries';
import { WsHeader } from './WsHeader';
import { Journal } from './Journal';
import { GoalRail, ItemRail } from './Rail';

function Pane({ kind, id, onClose, onBack, backLabel, push, onNewItem }) {
  const { goals, initiatives } = useBiz();
  const entity = kind === 'goal' ? goals.find((g) => g.id === id) : initiatives.find((i) => i.id === id);
  const journal = useEntries(kind, id);
  useEffect(() => { if (!entity) onClose(); }, [entity, onClose]);
  // Status changes, reviews and moves write system/review entries server-side — refresh the journal when the entity changes.
  const stamp = entity ? `${entity.updated_at}|${entity.last_activity_at || ''}` : '';
  const firstStamp = useRef(stamp);
  useEffect(() => { if (stamp && stamp !== firstStamp.current) { firstStamp.current = stamp; journal.reload(); } }, [stamp]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!entity) return null;
  const canPromote = kind === 'goal' || !!entity.goal_id;
  const openItem = (iid) => push({ kind: 'item', id: iid });
  const openGoal = (gid) => push({ kind: 'goal', id: gid });
  return (
    <>
      <WsHeader kind={kind} entity={entity} onClose={onClose} onBack={onBack} backLabel={backLabel} onOpenGoal={openGoal} />
      <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="lg:min-h-0 lg:overflow-y-auto px-5 lg:px-8 py-5" data-testid="ws-main"><Journal kind={kind} entity={entity} journal={journal} canPromote={canPromote} onOpenItem={openItem} /></div>
        <div className="lg:min-h-0 lg:overflow-y-auto px-5 lg:pl-0 lg:pr-8 py-5" style={{ background: 'rgba(42,39,34,0.025)' }}>
          {kind === 'goal' ? <GoalRail goal={entity} entries={journal.entries} onOpenItem={openItem} onNewItem={onNewItem} /> : <ItemRail item={entity} entries={journal.entries} onOpenGoal={openGoal} />}
        </div>
      </div>
    </>
  );
}

// Large window over the greyed dashboard. Keeps a small navigation stack so goal → initiative → back works.
export function Workspace({ start, onClose, onNewItem, onNavigate }) {
  const [stack, setStack] = useState([start]);
  const { goals, initiatives } = useBiz();
  useEffect(() => { setStack((s) => (s[s.length - 1].id === start.id ? s : [...s, start])); }, [start]);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !e.defaultPrevented && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  const cur = stack[stack.length - 1];
  const prev = stack.length > 1 ? stack[stack.length - 2] : null;
  useEffect(() => { onNavigate?.(cur.id); }, [cur.id, onNavigate]);
  const prevEntity = prev ? (prev.kind === 'goal' ? goals.find((g) => g.id === prev.id) : initiatives.find((i) => i.id === prev.id)) : null;
  const push = (next) => setStack((s) => (s[s.length - 1].id === next.id ? s : [...s, next]));
  return createPortal(
    <div className="biz-portal fixed inset-0 z-[60] flex items-center justify-center p-0 lg:p-6" style={{ background: 'rgba(42,39,34,0.55)', backdropFilter: 'blur(3px)', color: 'var(--ink)' }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="ws-backdrop">
      <div className="biz-drawer w-full h-full lg:h-[92vh] lg:max-w-[1480px] lg:rounded-3xl overflow-hidden flex flex-col shadow-2xl" style={{ background: 'var(--paper)', border: '1px solid var(--line)' }} data-testid="ws-window">
        <Pane key={`${cur.kind}-${cur.id}`} kind={cur.kind} id={cur.id} onClose={onClose} push={push} onNewItem={onNewItem}
          onBack={prev ? () => setStack((s) => s.slice(0, -1)) : null} backLabel={prevEntity ? `Back to ${prevEntity.title.length > 40 ? prevEntity.title.slice(0, 39) + '…' : prevEntity.title}` : 'Back'} />
      </div>
    </div>,
    document.body,
  );
}
